// Organisatiegegevens (Organisatie menu; PLAN-W29; FRONTEND-BLUEPRINT §3.7). The tenant admin views the
// Organisation master data and edits the LIMITED tenant-editable fields (trade name + contact). Legal
// name, registration number and VAT are admin-authored → shown read-only. Edit gated tenant_settings.update.
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Can } from "../../lib/rbac";
import { PageHeader } from "../../components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const errText = (e: unknown) => (e instanceof ApiError ? `Server ${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message);

interface OrgContact { address?: string | null; postalCode?: string | null; city?: string | null; country?: string | null; email?: string | null; phone?: string | null }
interface OrgDetails { id: string; legalName: string; tradeName: string | null; registrationNumber: string | null; vatNumber: string | null; contact: OrgContact }

const CONTACT_FIELDS = ["address", "postalCode", "city", "country", "email", "phone"] as const;

export function OrganisatiegegevensRoute() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["org-details"], queryFn: () => api.getOrganisationDetails() as Promise<OrgDetails> });

  const [tradeName, setTradeName] = useState("");
  const [contact, setContact] = useState<Record<string, string>>({});
  useEffect(() => {
    if (data) {
      setTradeName(data.tradeName ?? "");
      setContact(Object.fromEntries(CONTACT_FIELDS.map((k) => [k, data.contact?.[k] ?? ""])));
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.updateOrganisationDetails({
      tradeName: tradeName.trim() || null,
      contact: Object.fromEntries(CONTACT_FIELDS.map((k) => [k, contact[k]?.trim() || null])),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org-details"] }),
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t("org.title")} description={t("org.sub")} />
      {isLoading && <p className="text-sm text-muted-foreground">{t("common.loading")}</p>}
      {error && <p className="text-sm text-destructive">{errText(error)}</p>}
      {data && (
        <Card className="max-w-2xl">
          <CardHeader><CardTitle>{data.legalName}</CardTitle></CardHeader>
          <CardContent>
            <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
              {/* read-only master data (admin-authored) */}
              <div className="grid grid-cols-2 gap-4">
                <ReadOnly label={t("org.legalName")} value={data.legalName} />
                <ReadOnly label={t("org.registration")} value={data.registrationNumber} />
                <ReadOnly label={t("org.vat")} value={data.vatNumber} />
              </div>
              <p className="text-xs text-muted-foreground">{t("org.readonlyNote")}</p>

              <div className="grid gap-1.5">
                <Label htmlFor="org-trade">{t("org.tradeName")}</Label>
                <Input id="org-trade" value={tradeName} onChange={(e) => setTradeName(e.target.value)} maxLength={300} />
              </div>

              <fieldset className="grid gap-3 rounded-md border p-4">
                <legend className="px-1 text-sm font-medium">{t("org.contact")}</legend>
                <div className="grid grid-cols-2 gap-3">
                  {CONTACT_FIELDS.map((k) => (
                    <div className="grid gap-1.5" key={k}>
                      <Label htmlFor={`org-${k}`}>{t(`org.contact.${k}`)}</Label>
                      <Input id={`org-${k}`} value={contact[k] ?? ""} onChange={(e) => setContact((c) => ({ ...c, [k]: e.target.value }))} maxLength={300} />
                    </div>
                  ))}
                </div>
              </fieldset>

              {save.isError && <p className="text-sm text-destructive">{errText(save.error)}</p>}
              {save.isSuccess && <p className="text-sm text-emerald-600">{t("org.saved")}</p>}
              <Can perm="tenant_settings.update" fallback={<p className="text-sm text-muted-foreground">{t("settings.readonly")}</p>}>
                <div><Button type="submit" disabled={save.isPending}>{save.isPending ? t("crud.saving") : t("common.save")}</Button></div>
              </Can>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ReadOnly({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-muted-foreground">{label}</Label>
      <div className="text-sm">{value || "—"}</div>
    </div>
  );
}
